import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Lock, User, ShieldCheck, AlertTriangle, Check } from "lucide-react";
import { authApi } from "@/lib/auth";
import { Card, CardLabel, EmptyState, PageHeader } from "@/components/ui";
import AdminUserList from "@/components/AdminUserList";

const inputStyle: React.CSSProperties = {
  background: "var(--wf-paper)",
  border: "1px solid var(--wf-ink-5)",
  borderRadius: 8,
  padding: "10px 12px 10px 38px",
  fontSize: 14,
  color: "var(--wf-ink)",
  fontFamily: "var(--wf-font-body)",
  outline: "none",
  width: "100%",
};

function Field({
  label,
  hint,
  icon: IconCmp,
  children,
}: {
  label: string;
  hint?: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  children: (style: React.CSSProperties) => React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--wf-ink-3)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </span>
      {hint && (
        <span style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: -3 }}>
          {hint}
        </span>
      )}
      <div style={{ position: "relative" }}>
        <IconCmp
          size={14}
          strokeWidth={1.75}
          color="var(--wf-ink-3)"
          {...({
            style: {
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            },
          } as { style: React.CSSProperties })}
        />
        {children(inputStyle)}
      </div>
    </label>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => authApi.me(),
  });

  const createUser = useMutation({
    mutationFn: () => authApi.signUpEmail({ name, email, password }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setName("");
      setEmail("");
      setPassword("");
    },
  });

  if (meQuery.isLoading) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">verificando permissões…</span>
      </div>
    );
  }

  // Bloqueio UI: só admin acessa
  if (!meQuery.data?.isAdmin) {
    return (
      <div>
        <PageHeader
          icon={ShieldCheck}
          kicker="Admin"
          title="Acesso restrito"
          maxWidth={760}
        />
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 24px 64px" }}>
          <EmptyState
            icon={AlertTriangle}
            title="Esta página é só pra administradores"
            description="Se você precisa criar uma conta pra alguém da sua equipe, fale com o admin do Mohdus."
            action={
              <button
                type="button"
                onClick={() => navigate("/painel")}
                className="wf-btn wf-btn--ghost wf-btn--sm"
              >
                Voltar pro painel
              </button>
            }
          />
        </div>
      </div>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    createUser.mutate();
  }

  return (
    <div>
      <PageHeader
        icon={ShieldCheck}
        kicker="Admin"
        title="Criar novo usuário"
        maxWidth={760}
      />
      <div
        style={{
          maxWidth: 880,
          margin: "0 auto",
          padding: "24px 24px 64px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <Card>
          <CardLabel icon={<ShieldCheck size={13} color="var(--nd-orange)" />}>
            Acesso de administrador
          </CardLabel>
          <p
            style={{
              fontSize: 13,
              color: "var(--wf-ink-2)",
              lineHeight: 1.55,
              marginTop: 0,
              marginBottom: 0,
            }}
          >
            Você é admin desta instância ({meQuery.data.email}). Use este formulário pra
            criar contas pra membros da sua equipe. Eles vão receber as credenciais que
            você definir aqui.
          </p>
        </Card>

        <Card padding={24}>
          <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Field label="Nome" icon={User}>
              {(style) => (
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={style}
                  placeholder="Nome completo da pessoa"
                />
              )}
            </Field>

            <Field
              label="E-mail"
              hint="Vai ser o login da pessoa. Único na instância."
              icon={Mail}
            >
              {(style) => (
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={style}
                  placeholder="email@empresa.com"
                />
              )}
            </Field>

            <Field
              label="Senha temporária"
              hint="Mínimo 8 caracteres. Compartilha com a pessoa por canal seguro."
              icon={Lock}
            >
              {(style) => (
                <input
                  type="text"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={style}
                  placeholder="••••••••"
                />
              )}
            </Field>

            {createUser.isError && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  fontSize: 12,
                  color: "var(--nd-orange-pun)",
                  background: "#fff1e6",
                  border: "1px solid var(--nd-orange)",
                  borderRadius: 8,
                  padding: "10px 12px",
                  lineHeight: 1.5,
                }}
              >
                <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} />
                <span>{createUser.error.message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={createUser.isPending}
              className="wf-btn wf-btn--primary"
              style={{
                marginTop: 4,
                opacity: createUser.isPending ? 0.6 : 1,
                cursor: createUser.isPending ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "11px 16px",
                fontSize: 14,
              }}
            >
              {createUser.isPending ? "Criando…" : "Criar usuário"}
              {!createUser.isPending && <Check size={13} strokeWidth={2} color="#fff" />}
            </button>
          </form>
        </Card>

        <AdminUserList />

        <p
          style={{
            fontSize: 11,
            color: "var(--wf-ink-3)",
            textAlign: "center",
            marginTop: 8,
            lineHeight: 1.55,
          }}
        >
          Esta etapa é temporária pro período de testes. Quando o checkout/pagamento
          entrar, os usuários se cadastram sozinhos depois de pagar.
        </p>
      </div>
    </div>
  );
}
